import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { goToSection } from "@/lib/scroll";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "projects", label: "Projects" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => goToSection(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo-button"
            onClick={() => go("top")}
            className="cursor-pointer"
            aria-label="Back to top"
          >
            <Logo />
          </button>

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm tracking-wide text-[#a3a3a3] hover:text-[#f5f5f0] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-quote-cta"
              onClick={() => go("quote")}
              className="group flex items-center gap-2 bg-[#c5832b] hover:bg-[#d9953c] text-[#121212] text-sm font-semibold px-6 py-3 rounded-[2px] transition-colors duration-300"
            >
              Get a quote
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-[#f5f5f0] p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#121212]/97 backdrop-blur-xl flex flex-col"
          >
            <div className="px-6 h-20 flex items-center justify-between">
              <Logo />
              <button
                data-testid="nav-mobile-close"
                className="text-[#f5f5f0] p-2"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-5xl text-[#f5f5f0] py-4 border-b border-white/10 hover:text-[#c5832b] transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-mobile-quote-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                onClick={() => go("quote")}
                className="mt-8 bg-[#c5832b] text-[#121212] font-semibold px-8 py-4 rounded-[2px]"
              >
                Get a quote
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
